import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { fetchEdificios } from '@/services/edificios.service';
import { Building2, MapPin, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';

const PqrFormModal = ({ isOpen, onClose, onSubmit, initialData = null, loading = false }) => {
  const [formData, setFormData] = useState({
    tipo: '',
    categoria: '',
    origenTipo: '',
    edificioId: '',
    edificioNombre: '',
    edificioCiudad: '',
    apartamento: '',
    descripcion: '',
    prioridad: 'Media',
    reportaNombre: '',
    reportaCorreo: '',
    reportaTelefono: ''
  });

  const [errors, setErrors] = useState({});
  const [edificios, setEdificios] = useState([]);
  const [loadingEdificios, setLoadingEdificios] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadEdificios();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        tipo: initialData.tipo || '',
        categoria: initialData.categoria || '',
        origenTipo: initialData.origen?.tipo || '',
        edificioId: initialData.origen?.propiedad?.edificio?.id || '',
        edificioNombre: initialData.origen?.propiedad?.edificio?.nombre || '',
        edificioCiudad: initialData.origen?.propiedad?.edificio?.ciudad || '',
        apartamento: initialData.origen?.apartamento || '',
        descripcion: initialData.descripcion || '',
        prioridad: initialData.prioridad || 'Media',
        reportaNombre: initialData.usuarioReporta?.nombre || '',
        reportaCorreo: initialData.usuarioReporta?.correo || '',
        reportaTelefono: initialData.usuarioReporta?.telefono || ''
      });
    } else {
      setFormData({
        tipo: '',
        categoria: '',
        origenTipo: '',
        edificioId: '',
        edificioNombre: '',
        edificioCiudad: '',
        apartamento: '',
        descripcion: '',
        prioridad: 'Media',
        reportaNombre: '',
        reportaCorreo: '',
        reportaTelefono: ''
      });
    }
    setErrors({});
    setSelectedImages([]);
  }, [initialData, isOpen]);

  const loadEdificios = async () => {
    setLoadingEdificios(true);
    try {
      const response = await fetchEdificios({ limit: 1000, offset: 1, query: {} });
      if (response && response.data) {
        setEdificios(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar edificios:', error);
    } finally {
      setLoadingEdificios(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tipo) {
      newErrors.tipo = 'El tipo es requerido';
    }

    if (!formData.categoria) {
      newErrors.categoria = 'La categoría es requerida';
    }

    if (!formData.origenTipo) {
      newErrors.origenTipo = 'El origen del PQR es requerido';
    }


    if (!formData.descripcion || formData.descripcion.trim() === '') {
      newErrors.descripcion = 'La descripción es requerida';
    }

    if (!formData.prioridad) {
      newErrors.prioridad = 'La prioridad es requerida';
    }

    if (!formData.reportaNombre || formData.reportaNombre.trim() === '') {
      newErrors.reportaNombre = 'El nombre de quien reporta es requerido';
    }

    if (formData.reportaCorreo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.reportaCorreo)) {
      newErrors.reportaCorreo = 'El correo electrónico no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setSelectedImages(prev => [...prev, ...imageFiles]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({ ...formData, selectedImages });
    }
  };

  const handleChange = (field, value) => {
    // Si se está seleccionando un edificio, guardar también su nombre y ciudad
    if (field === 'edificioId') {
      const edificioSeleccionado = edificios.find(e => e._id === value);
      setFormData(prev => ({
        ...prev,
        edificioId: value,
        edificioNombre: edificioSeleccionado?.nombre || '',
        edificioCiudad: edificioSeleccionado?.ciudad || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Editar PQR' : 'Nueva PQR'}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2">
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo y Categoría en grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tipo */}
            <div className="space-y-2">
              <Label htmlFor="tipo">
                Tipo <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => handleChange('tipo', value)}
              >
                <SelectTrigger id="tipo" className={errors.tipo ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccione el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Petición">Petición</SelectItem>
                  <SelectItem value="Queja">Queja</SelectItem>
                  <SelectItem value="Reclamo">Reclamo</SelectItem>
                </SelectContent>
              </Select>
              {errors.tipo && (
                <p className="text-sm text-red-500">{errors.tipo}</p>
              )}
            </div>

            {/* Categoría */}
            <div className="space-y-2">
              <Label htmlFor="categoria">
                Categoría <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.categoria}
                onValueChange={(value) => handleChange('categoria', value)}
              >
                <SelectTrigger id="categoria" className={errors.categoria ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccione la categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Logística">Logística</SelectItem>
                  <SelectItem value="Calidad del lavado">Calidad del lavado</SelectItem>
                  <SelectItem value="Entregas">Entregas</SelectItem>
                  <SelectItem value="Faltantes">Faltantes</SelectItem>
                  <SelectItem value="Reprocesos">Reprocesos</SelectItem>
                  <SelectItem value="Insumos">Insumos</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
              {errors.categoria && (
                <p className="text-sm text-red-500">{errors.categoria}</p>
              )}
            </div>
          </div>

          {/* Origen del PQR y Prioridad en grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Origen del PQR */}
            <div className="space-y-2">
              <Label htmlFor="origenTipo">
                Origen del PQR <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.origenTipo}
                onValueChange={(value) => handleChange('origenTipo', value)}
              >
                <SelectTrigger id="origenTipo" className={errors.origenTipo ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccione el origen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Lavandería">Lavandería</SelectItem>
                  <SelectItem value="Ruta / Conductor">Ruta / Conductor</SelectItem>
                  <SelectItem value="Apartamento">Apartamento</SelectItem>
                </SelectContent>
              </Select>
              {errors.origenTipo && (
                <p className="text-sm text-red-500">{errors.origenTipo}</p>
              )}
            </div>

            {/* Prioridad */}
            <div className="space-y-2">
              <Label htmlFor="prioridad">
                Prioridad <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.prioridad}
                onValueChange={(value) => handleChange('prioridad', value)}
              >
                <SelectTrigger id="prioridad" className={errors.prioridad ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccione la prioridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Baja">Baja</SelectItem>
                </SelectContent>
              </Select>
              {errors.prioridad && (
                <p className="text-sm text-red-500">{errors.prioridad}</p>
              )}
            </div>
          </div>

          {/* Información del Apartamento */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-700 font-medium">
                <Building2 className="h-4 w-4" />
                <span>Información del Apartamento</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Selector de Edificio */}
                <div className="space-y-2">
                  <Label htmlFor="edificioId">
                    Edificio <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.edificioId}
                    onValueChange={(value) => handleChange('edificioId', value)}
                    disabled={loadingEdificios}
                  >
                    <SelectTrigger id="edificioId" className={errors.edificioId ? 'border-red-500' : ''}>
                      <SelectValue placeholder={loadingEdificios ? "Cargando edificios..." : "Seleccione el edificio"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {edificios.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No hay edificios disponibles
                        </div>
                      ) : (
                        edificios.map((edificio) => (
                          <SelectItem key={edificio._id} value={edificio._id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{edificio.nombre}</span>
                              <span className="text-xs text-gray-500">
                                {edificio.ciudad} • {edificio.direccion}
                              </span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.edificioId && (
                    <p className="text-sm text-red-500">{errors.edificioId}</p>
                  )}
                </div>

                {/* Apartamento específico - Texto libre (Opcional) */}
                <div className="space-y-2">
                  <Label htmlFor="apartamento">
                    Apartamento específico
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="apartamento"
                      type="text"
                      value={formData.apartamento}
                      onChange={(e) => handleChange('apartamento', e.target.value)}
                      placeholder="Ej: Apto 101, Habitación 205..."
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

          {/* Separador - Información de quien reporta */}
          <div className="pt-4 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Información de quien reporta</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre de quien reporta */}
              <div className="space-y-2">
                <Label htmlFor="reportaNombre">
                  Nombre completo <span className="text-red-500">*</span>
                </Label>
                <input
                  id="reportaNombre"
                  type="text"
                  value={formData.reportaNombre}
                  onChange={(e) => handleChange('reportaNombre', e.target.value)}
                  placeholder="Nombre de quien reporta"
                  className={`flex h-10 w-full rounded-md border ${errors.reportaNombre ? 'border-red-500' : 'border-input'} bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                />
                {errors.reportaNombre && (
                  <p className="text-sm text-red-500">{errors.reportaNombre}</p>
                )}
              </div>

              {/* Correo electrónico */}
              <div className="space-y-2">
                <Label htmlFor="reportaCorreo">
                  Correo electrónico
                </Label>
                <input
                  id="reportaCorreo"
                  type="email"
                  value={formData.reportaCorreo}
                  onChange={(e) => handleChange('reportaCorreo', e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className={`flex h-10 w-full rounded-md border ${errors.reportaCorreo ? 'border-red-500' : 'border-input'} bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                />
                {errors.reportaCorreo && (
                  <p className="text-sm text-red-500">{errors.reportaCorreo}</p>
                )}
              </div>

              {/* Teléfono */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="reportaTelefono">
                  Teléfono de contacto
                </Label>
                <input
                  id="reportaTelefono"
                  type="tel"
                  value={formData.reportaTelefono}
                  onChange={(e) => handleChange('reportaTelefono', e.target.value)}
                  placeholder="Número de teléfono"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">
              Descripción <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Describa detalladamente el incidente..."
              rows={4}
              className={errors.descripcion ? 'border-red-500' : ''}
            />
            {errors.descripcion && (
              <p className="text-sm text-red-500">{errors.descripcion}</p>
            )}
          </div>

          {/* Imágenes adjuntas */}
          <div className="space-y-2">
            <Label>Imágenes adjuntas (opcional)</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  id="pqr-image-upload"
                  disabled={loading}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('pqr-image-upload').click()}
                  disabled={loading}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Seleccionar imágenes
                </Button>
              </div>
              
              {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {selectedImages.map((file, index) => (
                    <div key={index} className="relative border rounded-lg p-2 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveImage(index)}
                        disabled={loading}
                        className="h-6 w-6"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
        </div>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Guardando...' : initialData ? 'Actualizar' : 'Crear PQR'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PqrFormModal;
